import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  HStack,
  useToast,
  Box,
  Text,
  VStack
} from '@chakra-ui/react';

interface UpdateNotificationProps {
  onUpdate?: () => void;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ onUpdate }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const toast = useToast();

  useEffect(() => {
    // Skip SW registration in development so hot-reload always reflects latest changes
    if (process.env.NODE_ENV === 'development') return;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => {
          setRegistration(reg);
          
          // Check for updates every week (7 days)
          const interval = setInterval(() => {
            reg.update();
          }, 7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds

          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                  toast({
                    position: 'top',
                    render: () => (
                      <Box
                        bg="primary.surfaceVariant"
                        color="text.primary"
                        p={4}
                        borderRadius="medium"
                        boxShadow="medium"
                        border="1px solid"
                        borderColor="accent.secondary"
                        backdropFilter="blur(10px)"
                        minW="300px"
                      >
                        <HStack spacing={3}>
                          <Box
                            bg="accent.secondary"
                            borderRadius="full"
                            p={2}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="lg" color="text.primary">🚀</Text>
                          </Box>
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="600" fontSize="md" color="text.primary">Update Available!</Text>
                            <Text fontSize="sm" color="text.secondary">A new version of the app is ready.</Text>
                          </VStack>
                        </HStack>
                      </Box>
                    ),
                    duration: 3000,
                  });
                }
              });
            }
          });

          return () => clearInterval(interval);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, [toast]);

    const handleUpdate = async () => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      setUpdateAvailable(false);
      
      // Force cache clear and reload
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'FORCE_UPDATE' });
      }
      
      try {
        // Clear browser cache if available
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
      } catch (error) {
        console.log('Cache clearing failed:', error);
      }
      
      // Reload after clearing cache
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
      if (onUpdate) {
        onUpdate();
      }
    }
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <Box position="fixed" top="4" left="4" right="4" zIndex="9999">
      <Alert status="info" borderRadius="lg" boxShadow="lg">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle fontSize="sm">Update Available!</AlertTitle>
          <AlertDescription fontSize="xs">
            A new version with improvements is ready to install.
          </AlertDescription>
        </Box>
        <HStack spacing={2}>
          <Button size="sm" colorScheme="blue" onClick={handleUpdate}>
            Update Now
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDismiss}>
            Later
          </Button>
        </HStack>
      </Alert>
    </Box>
  );
};

export default UpdateNotification; 